import { produce } from 'immer';

import { authenticatedImageSlice } from '@tdp/common/authenticatedImages';
import { createCommands } from '@tdp/common/commandHandler';
import { ApiFetchStatus } from '@tdp/common/constants';
import { stringToBlob } from '@tdp/common/downloadUtils/downloadFile';
import { LongRunningOperation, lroSlice } from '@tdp/common/lro';
import { notificationSlice, NotificationType } from '@tdp/common/notification';
import { ILogger } from '@tdp/common/telemetry';

import { AppPublishLro, IAppDetails, IAppDetailsLanguageFile, IAppDetailsReader, IAppDetailsWriter, IAppPublishReader, IAppPublishWriter, IAppIconWriter } from './abstractions';
import { IAppLanguageWriter } from './abstractions/AppLanguageAbstractions';
import { IAppOwnersWriter } from './abstractions/AppOwnersAbstractions';
import { IAppOwner } from './abstractions/IAppOwners';
import { appDetailsSlice, appPublishingSlice } from './slice';
import { IAppListReader } from '../appList/abstractions/IAppListReader';

interface AppDetailsStoreState { [appDetailsSlice.name]: ReturnType<typeof appDetailsSlice.getInitialState> }
interface AuthenticatedImagesStoreState { [authenticatedImageSlice.name]: ReturnType<typeof authenticatedImageSlice.getInitialState> }

export interface CreateAppDetailCommandHandlerDependencies {
  logger: ILogger;
  appReader: IAppDetailsReader;
  appWriter: IAppDetailsWriter;
  appPublishReader: IAppPublishReader;
  appPublishWriter: IAppPublishWriter;
  appListReader: IAppListReader;
  appLanguageWriter: IAppLanguageWriter;
  appOwnersWriter: IAppOwnersWriter;
  appIconWriter: IAppIconWriter;
}
export const createAppDetailCommandHandler = ({
  appReader,
  appWriter,
  appPublishReader,
  appPublishWriter,
  logger,
  appListReader,
  appLanguageWriter,
  appOwnersWriter,
  appIconWriter,
}: CreateAppDetailCommandHandlerDependencies) =>
  createCommands({
    name: 'appDetailCommands',
    handlers: {
      async *loadApp({ id }: { id: string }) {
        yield appDetailsSlice.actions.appDetailsLoading();
        const appDetailsResult = await appReader.readAppDetails(id);
        if (appDetailsResult.type === 'error') {
          yield appDetailsSlice.actions.appDetailsLoadingFailed();
          logger.error(
            'Error loading app details for app id: %s | reason: %s',
            id,
            appDetailsResult.errorMessage
          );
          yield notificationSlice.actions.showNotification({
            message: `Failed to load app: ${id} | ${appDetailsResult.errorType}`,
            show: true,
            autoDismiss: false,
            type: NotificationType.Error,
          });
          return;
        }
        yield appDetailsSlice.actions.loadApp(appDetailsResult.appDetails);
        yield appDetailsSlice.actions.appDetailsLoadingSuccess();
      },

      // App publishing. (TODO(nibeauli): refactor out along with all the app publishing into its own silo)
      async *loadLobAppPublishStatus({ id }: { id: string }) {
        const response = await appPublishReader.readLobAppPublishStatus(id);
        if (response.type === ApiFetchStatus.SUCCESS && response.response) {
          yield appPublishingSlice.actions.appLobPublishSuccess({ id: id, response: response.response, });
          return;
        }
        yield notificationSlice.actions.showNotification({
          message: `Failed to get publish status for ${id}`,
          show: true,
          autoDismiss: true,
          type: NotificationType.Error,
        });
        return;
      },
      async *createLobPublishRequest({ lro, appId }: {
        lro: LongRunningOperation<AppPublishLro>;
        appId: string;
      }) {
        yield lroSlice.actions.lroCreated(lro);
        const response = await appListReader.downloadApp(appId);
        if (response.type !== ApiFetchStatus.SUCCESS) {
          yield notificationSlice.actions.showNotification({
            message: `Failed to download app package: ${appId}`,
            show: true,
            autoDismiss: true,
            type: NotificationType.Error,
          });
          return;
        }
        yield lroSlice.actions.lroUpdated(
          produce(lro, (nextLro) => {
            nextLro.state.packageDownloaded = true;
          })
        );
        const blob = await stringToBlob(response.appPackage);
        const publishRequest = await appPublishWriter.createLobPublishRequest(blob);
        if (publishRequest.type !== ApiFetchStatus.SUCCESS) {
          yield notificationSlice.actions.showNotification({
            message: `Failed to create publish request: ${appId}`,
            show: true,
            autoDismiss: true,
            type: NotificationType.Error,
          });
        }
        yield lroSlice.actions.lroUpdated(
          produce(lro, (nextLro) => {
            nextLro.state.requestSubmitted = true;
          })
        );
        return;
      },
      async *deleteLobAppPublishRequest({ id, appId, internalId }: { id: string; appId: string; internalId: string }) {
        const response = await appPublishWriter.deleteLobAppPublishRequest(id, internalId);
        if (response.type === ApiFetchStatus.SUCCESS) {
          yield appPublishingSlice.actions.appLobRequestDeleteSuccess(appId);
          return;
        }
        yield notificationSlice.actions.showNotification({
          message: `Failed to delete publish status for ${appId}`,
          show: true,
          autoDismiss: true,
          type: NotificationType.Error,
        });
        return;
      },
      async *writeApp({ appDetails }: { appDetails: IAppDetails }) {
        const appDetailsResult = await appWriter.writeAppDetails(appDetails);
        if (appDetailsResult.type === 'error') {
          logger.error(
            'Error loading app details for app id: %s | reason: %s',
            appDetails.appId,
            appDetailsResult.errorMessage
          );
          logger.logUserBi({
            telemetryMarker: 'api-action-write-app-failed', message: `Failed to write app details for app id: ${appDetails.appId}`,
          });
          yield notificationSlice.actions.showNotification({
            message: `Failed to write app: ${appDetails.appId} | ${appDetailsResult.errorType}`,
            show: true,
            autoDismiss: false,
            type: NotificationType.Error,
          });
          return;
        }
        logger.logUserBi({
          telemetryMarker: 'api-action-write-app-success', message: `Successfully wrote app details for app id: ${appDetails.appId}`,
        });
        yield notificationSlice.actions.showNotification({
          message: `App ${appDetails.shortName ?? ''} updated successfully`,
          show: true,
          autoDismiss: true,
          type: NotificationType.Success,
        });
        yield appDetailsSlice.actions.loadApp(appDetails);
        yield appDetailsSlice.actions.clearForm({ id: appDetails.appId });
      },

      // App publishing. (TODO(oskhare): refactor out along with all the app language into its own silo or wait for service re-arch)
      async *deleteLanguage({ id, appId }: { id: string; appId: string }) {
        const response = await appLanguageWriter.deleteLanguage(id, appId);
        if (response.type === ApiFetchStatus.SUCCESS) {
          yield notificationSlice.actions.showNotification({
            message: `Successfully deleted language ${id}`,
            show: true,
            autoDismiss: true,
            type: NotificationType.Success,
          });
          return;
        }
        yield notificationSlice.actions.showNotification({
          message: `Failed to delete language for ${appId}`,
          show: true,
          autoDismiss: true,
          type: NotificationType.Error,
        });
        return;
      },
      async *updateLanguage({ id, appId, language, isNew }: { id: string; appId: string; language: IAppDetailsLanguageFile, isNew: boolean }) {
        const response = await appLanguageWriter.addOrUpdateLanguage(id, appId, language, isNew);
        if (response.type === ApiFetchStatus.SUCCESS) {
          yield notificationSlice.actions.showNotification({
            message: `Successfully updated language ${id}`,
            show: true,
            autoDismiss: true,
            type: NotificationType.Success,
          });
          return;
        }
        yield notificationSlice.actions.showNotification({
          message: `Failed to update language for ${appId}`,
          show: true,
          autoDismiss: true,
          type: NotificationType.Error,
        });
      },

      async *updateOwners({ payload, appId, resign }: { payload: IAppOwner[], appId: string, resign?: boolean }) {
        const response = await appOwnersWriter.updateOwners(payload, appId, resign);
        if (response.type === ApiFetchStatus.SUCCESS) {
          yield notificationSlice.actions.showNotification({
            message: `Successfully updated owners`,
            show: true,
            autoDismiss: true,
            type: NotificationType.Success,
          });
          return;
        }
        yield notificationSlice.actions.showNotification({
          message: `Failed to update owners`,
          show: true,
          autoDismiss: true,
          type: NotificationType.Error,
        });
      },
      async *updateAppIcon({ appId, icon, type }: { appId: string, icon: (File | "delete"), type: 'color' | 'outline' }, getState) {
        const currentState = getState() as AppDetailsStoreState & AuthenticatedImagesStoreState;
        const iconPath = `/appdefinitions/${appId}/icons/${type}`;
        const currentIcon = authenticatedImageSlice.selectors.selectAuthenticatedImage(currentState, iconPath);
        if (icon === "delete") {
          if (currentIcon?.type == "loaded") {
            URL.revokeObjectURL(currentIcon.dataUri);
          }
          yield authenticatedImageSlice.actions.authenticatedImageRemoved({
            path: iconPath,
          });
          await appIconWriter.deleteAppIcon(appId, type);
        } else {
          if (currentIcon?.type !== "loaded") {
            logger.trace('Loaded new app icon for app %s', appId);
            yield authenticatedImageSlice.actions.authenticatedImageLoaded({
              path: iconPath,
              dataUri: URL.createObjectURL(icon),
            });
          } else {
            yield authenticatedImageSlice.actions.authenticatedImageReplaced({
              path: iconPath,
              dataUri: URL.createObjectURL(icon),
            });
          }

          const result = await appIconWriter.updateAppIcon(appId, icon, type);
          if (result.type === 'error') {
            yield notificationSlice.actions.showNotification({
              show: true,
              autoDismiss: true,
              type: NotificationType.Error,
              message: `Failed to update app icon: ${result.errorMessage}`,
            });
            //TODO(nibeauli): rollback the replacement?
            return;
          }
        }
      }
    }
  });
